import { FC, useEffect, useState } from 'react';
import { Modal } from '../../../../componentsV2/Modal';
import { Select } from 'antd';
import Styles from './styles';
import { MultipleAccountsSelector } from '../../../../pages/accounts2/components/MultipleAccountsSelector';
import { coreService } from '../../../../services/core/core-service';
import { DateSelect } from '../../../../pages/home/FilterBar/DateSelect';
import { sortDates } from '../../../../utils/dateUtils';
import { useToast } from '../../../../hooks/useToast';
import { Alert } from '../../../../components/alerts/Alert';
import { useFrameworkReporting } from '../../hooks/use-framework-reporting';
import { useOrganizationMembers } from '../../../organizations/hooks/useOrganizationMembers';
import { SelectAll } from '../../../../pages/insights/components/SelectAll';
import { useAnalytics } from '../../../analytics/hooks/useAnalytics';

const { Container, FormItem, Label } = Styles;

export type RunReportModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  predefinedValues?: {
    framework: string | null;
    type: string;
    accounts?: string[];
    csmIds?: string[];
  };
};

export const RunReportModal: FC<RunReportModalProps> = ({
  open,
  onClose,
  onSuccess,
  predefinedValues,
}) => {
  const MAX_ALLOWED_ACCOUNTS = 20;

  const { sendEvent } = useAnalytics();
  const { frameworks, isFetchingFrameworks } = useFrameworkReporting();
  const { members } = useOrganizationMembers();

  const { success, error } = useToast();

  const [framework, setFramework] = useState<string | null>(null);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [csms, setCsms] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [reportType, setReportType] = useState<string>('account');

  const reportTypeOptions = [
    { label: 'Account', value: 'account' },
    { label: 'Scorecard', value: 'scorecard' },
  ];

  useEffect(() => {
    if (predefinedValues) {
      setFramework(predefinedValues.framework);
      setReportType(predefinedValues.type);
      setAccounts(predefinedValues.accounts ?? []);
      setCsms(predefinedValues.csmIds ?? []);
    }
  }, [predefinedValues]);

  const clearForm = () => {
    setAccounts([]);
    setFramework(null);
    setStartDate(null);
    setEndDate(null);
  };

  const handleRun = async () => {
    try {
      setIsLoading(true);
      await coreService.runFrameworkReportingExecution({
        frameworkId: framework!,
        from: startDate!.toISOString(),
        to: endDate!.toISOString(),
        type: reportType,
        parameters: {
          accountIds: reportType === 'account' ? accounts : undefined,
          csmIds: reportType === 'scorecard' ? csms : undefined,
        },
      });
      onSuccess();
      sendEvent(`generate ${reportType} report`);
      success("Request submitted successfully. We'll email you when your report is ready.");
      clearForm();
    } catch (err) {
      error('Failed to generate report');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendarChange = (values: [Date, Date] | [null, null]) => {
    if (values[0] !== null && values[1] !== null) {
      const [firstDate, secondDate] = sortDates(values);
      setStartDate(firstDate);
      setEndDate(secondDate);
    }
  };

  const handleClose = () => {
    onClose();
    clearForm();
  };

  const isFormInvalid =
    !framework ||
    (reportType === 'account' && !accounts.length) ||
    !startDate ||
    !endDate ||
    (reportType === 'account' && accounts.length > MAX_ALLOWED_ACCOUNTS);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Run Report"
      width={500}
      zIndex={1000}
      primaryAction={{
        label: 'Run',
        onClick: handleRun,
        disabled: isFormInvalid,
        loading: isLoading,
      }}
      secondaryAction={{ label: 'Cancel', onClick: handleClose }}
    >
      <Container>
        {accounts.length >= MAX_ALLOWED_ACCOUNTS && (
          <Alert
            title=""
            message={`You can select up to ${MAX_ALLOWED_ACCOUNTS} accounts`}
            variant="info"
            closable
          />
        )}

        <FormItem>
          <Label>Framework</Label>
          <Select
            value={framework}
            onChange={setFramework}
            placeholder="Select a framework"
            loading={isFetchingFrameworks}
            options={
              frameworks?.map(framework => ({
                label: framework.name,
                value: framework.id,
              })) ?? []
            }
          />
        </FormItem>

        <FormItem>
          <Label>Report Type</Label>
          <Select
            value={reportType}
            onChange={setReportType}
            placeholder="Select a report type"
            options={reportTypeOptions}
          />
        </FormItem>

        {reportType === 'account' && (
          <FormItem>
            <Label>
              Accounts{' '}
              <small>
                <em>(up to {MAX_ALLOWED_ACCOUNTS})</em>
              </small>
            </Label>
            <MultipleAccountsSelector onSelect={setAccounts} searchType="local" value={accounts} />
          </FormItem>
        )}

        {reportType === 'scorecard' && (
          <FormItem>
            <Label>
              CMSs{' '}
              <small style={{ color: 'gray' }}>
                <em>(by leaving the field blank, all CSMs will be considered for the report)</em>
              </small>
            </Label>
            <SelectAll
              label={'CSMs'}
              value={csms}
              options={
                members?.map(m => ({
                  id: m.user.id,
                  label: m.user.firstName + ' ' + m.user.lastName,
                })) || []
              }
              onSelectionChanged={setCsms}
            />
          </FormItem>
        )}

        <FormItem>
          <Label>Start Date</Label>
          <DateSelect
            key={startDate?.toISOString() && endDate?.toISOString()}
            style={{ fontFamily: 'Cera Pro' }}
            bordered={true}
            pastOnly={true}
            futureOnly={false}
            handleCalendarChange={handleCalendarChange}
            defaultFrom={startDate}
            defaultTo={endDate}
          />
        </FormItem>
      </Container>
    </Modal>
  );
};
